import i18n from '@assets/i18n/config';
import type { CurrencyCodes, Jurisdictions } from '@onevesthq/ov-enums';
import type { AccountTypes } from './account';
import type { Localization } from './localization';
import type { Role } from './role';
import type { TranslatedString } from './shared';
import { TRADING_PROCESS_LIST } from './tradingProcessList';

export type OrganizationTheme = {
  sideBarColor?: string;
  logo?: string;
  authenticationTheme: {
    logo?: string;
    primaryColor?: string;
    pageBackgroundColor?: string;
  };
};

export enum AuthenticationConnections {
  EMAIL_AND_PASSWORD = 'EMAIL_AND_PASSWORD',
  EMAIL_OTP = 'EMAIL_OTP',
  ENTERPRISE = 'ENTERPRISE',
}

export enum OrgBulkTradingProcesses {
  PAIR = 'PAIR',
  BUCKET = 'BUCKET',
}

export type EnabledJurisdictions = {
  all: boolean;
  only?: Jurisdictions[];
};

export type OrganizationAuthenticationDomain = {
  url?: string;
  authenticationType?: AuthenticationConnections;
  enterpriseConnectionName?: string;
  useHostedLogin?: boolean;
  logoutUrl?: string;
};

export enum MultiFactorAuthenticationOptions {
  AUTHENTICATOR = 'AUTHENTICATOR',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

export type PublicOrganization = {
  id: string;
  name?: string;
  translatedName?: TranslatedString;
  subdomain: string;
  browserTabTitle?: string;
  clientHostedLoginClientId?: string;
  clientHostedLoginScope?: string;
  clientHostedLoginURL?: string;
  faviconLink?: string;
  parent?: PublicOrganization;
  theme?: OrganizationTheme;
};

export type Organization = {
  id: string;
  name?: string;
  translatedName?: TranslatedString;
  entityName?: string;
  subdomain?: string;
  parent?: Partial<Organization>;
  isReferral?: boolean;
  permissions?: [string];
  theme?: OrganizationTheme;
  useParentProductShelf?: boolean;
  useParentModelPortfolios?: boolean;
  useParentThemes?: boolean;
  useParentAssetClasses?: boolean;
  useParentSchedules?: boolean;
  useParentFeeTiers?: boolean;
  useParentBillingSchedules?: boolean;
  useParentRoleProfiles?: boolean;
  useParentWorkflows?: boolean;
  useParentPageConfigurations?: boolean;
  useParentStaticPageWidgetConfigurations?: boolean;
  useParentCustodianConnections?: boolean;
  useParentIntegrations?: boolean;
  useParentWebhooks?: boolean;
  useParentLocalizations?: boolean;
  useParentThemeTokens?: boolean;
  useParentArticles?: boolean;
  defaultAuthenticationConnection?: AuthenticationConnections;
  enableMultiFactorAuthentication?: boolean;
  multiFactorAuthenticationOptions?: MultiFactorAuthenticationOptions;
  enableRemeberBrowser?: boolean;
  enableEmailFrenchTranslation?: boolean;
  allowAccessToSubOrganizations?: boolean;
  availableFeatureFlags?: string[];
  reviewTransactions?: boolean;
  allowPortfolioPerGoal?: boolean;
  blockSuspiciousTransactions?: boolean;
  autoInviteImportedUsers?: boolean;
  repCode?: string;
  jurisdictions?: EnabledJurisdictions;
  localization?: Localization;
  applicableLocalization?: Localization;
  userManagementFeeBps?: number;
  minInitialDepositCents?: number;
  revenueShareBps?: number;
  revenueShareTaxes?: number;
  allowDirectCommunication?: boolean;
  allowInstantInvest?: boolean;
  requireFeeApproval?: boolean;
  allowChargePST?: boolean;
  useClientOrgAuth0?: boolean;
  minRecurringDepositCents?: number;
  profileReviewTimeInMonths?: number;
  allowInactiveUserRebalancing?: boolean;
  allowInactiveUserFeeCalculation?: boolean;
  allowInactiveUserAdjustments?: boolean;
  requireManualAccountApproval?: boolean;
  requireSubTradeRequestApproval?: boolean;
  allowViewSubTradeRequestPairs?: boolean;
  allowPendingTransactions?: boolean;
  translatedSupportUrl?: TranslatedString;
  translatedHelpCentreUrl?: TranslatedString;
  defaultTradingProcess?: any;
  bulkTradingProcess?: any;
  defaultSignUpRole?: Role;
  defaultAdvisorRole?: Role;
  defaultClientRole?: Role;
  disableStatsUpdateBasedOnTransactions?: boolean;
  historicalSnapshotsCutOffDate?: Date;
  excludeAdminFeesFromClients?: boolean;
  allowPostOptimizationEditing?: boolean;
  cashOnHoldToTradeDays?: number;
  cashOnHoldToWithdrawDays?: number;
  cancelTransferCutoffDays?: number;
  marketValueDipThresholdPercentage?: number;
  displayMenuBar?: boolean;
  allowLogout?: boolean;
  inactivityTimeoutInMinutes?: number;
  enableInactivityTimeOut?: boolean;
  auth0DisplayName?: string;
  termsAndConditionsUrl?: string;
  translatedTermsAndConditionsUrl?: TranslatedString;
  disableRebalanceReport?: boolean;
  logoutRedirectUrl?: string;
  enableSingleLogout?: boolean;
  useParentCustomFields?: boolean;
  useParentFormTemplates?: boolean;
  displayCurrency?: boolean;
  themeTokens?: any;
  customUrl?: string;
  authenticationDomains?: OrganizationAuthenticationDomain[];
  accountTypeTaxRanks?: [
    {
      id?: string;
      accountType: AccountTypes;
      taxRank: number;
    },
  ];
  faviconLink?: string;
  browserTabTitle?: string;
  minDepositCents?: number;
  errorPageMessage?: TranslatedString;
  errorPageDescription?: TranslatedString;
  errorPageButtonText?: TranslatedString;
  errorPageShowNavbar?: boolean;
  errorPageBackgroundColor?: string;
  errorPageBackgroundImage?: string;
  productShelfCurrencies?: CurrencyCodes[];
  enableContactSupport?: boolean;
  enableResourcesAndArticles?: boolean;
  enableManualUpdateExchangeRates?: boolean;
  enableAutoUpdateExchangeRates?: boolean;
  enableFullCashTransfers?: boolean;
  enablePartialCashTransfers?: boolean;
  enableAllInKindTransfers?: boolean;
  baseCurrency?: CurrencyCodes;
  hideAutoUpdatePrices?: boolean;
};

export const ORG_TRADING_PROCESS_LIST = [
  {
    value: 'INHERITED_FROM_PARENT',
    name: i18n.t('client:tradingProcessItems.INHERITED_FROM_PARENT'),
  },
  ...TRADING_PROCESS_LIST,
];
